/**
 * useOutreachAccess.js
 *
 * Liefert die Outreach-Job-Lizenz der aktiven World: ob das Feature überhaupt
 * freigeschaltet ist und welche Zustellarten gewählt werden dürfen.
 *
 * Die Flags liegen beim Owner der World (Users/{activeWorldId}/…), nicht beim
 * eingeloggten User. Ein Team-Mitarbeiter bekommt damit exakt die
 * Freischaltungen seines Owners. Gesetzt wird ausschliesslich im
 * Team-Dashboard (PremiumToggle).
 */

import { useEffect, useMemo, useState } from 'react';
import { ref, onValue } from 'firebase/database';
import { database } from '../firebaseConfig';
import { useTeam } from '../contexts/TeamContext';
import { resolveOutreachModes, OUTREACH_ALL_LOCKED } from '../config/outreachFeatures';

export const useOutreachAccess = () => {
  const { activeWorldId } = useTeam();

  // null = noch nicht geladen. Ohne diese Unterscheidung würde beim Öffnen des
  // Job-Builders kurz die falsche Zustellart als gesperrt aufblitzen.
  const [state, setState] = useState(null);

  useEffect(() => {
    if (!activeWorldId) {
      setState(null);
      return undefined;
    }

    setState(null);
    const partial = {};

    const commit = () => {
      if (partial.enabled === undefined || partial.modes === undefined) return;
      setState({ enabled: partial.enabled, modes: partial.modes });
    };

    const unsubEnabled = onValue(
      ref(database, `Users/${activeWorldId}/hasOutreachJobs`),
      (snapshot) => { partial.enabled = snapshot.val() === true; commit(); },
      () => { partial.enabled = false; commit(); }
    );

    const unsubModes = onValue(
      ref(database, `Users/${activeWorldId}/outreachModes`),
      (snapshot) => { partial.modes = resolveOutreachModes(snapshot.val()); commit(); },
      () => { partial.modes = { ...OUTREACH_ALL_LOCKED }; commit(); }
    );

    return () => { unsubEnabled(); unsubModes(); };
  }, [activeWorldId]);

  return useMemo(() => {
    const modes = state ? state.modes : OUTREACH_ALL_LOCKED;
    // Ohne Master-Flag zählt keine Zustellart, egal was darunter gesetzt ist.
    const enabled = !!state && state.enabled;
    return {
      outreachEnabled: enabled,
      canSend: enabled && modes.send === true,
      canPrepare: enabled && modes.prepare === true,
      outreachLoading: !!activeWorldId && state === null,
    };
  }, [state, activeWorldId]);
};

export default useOutreachAccess;
